class ZendeskReturnsForm extends HTMLElement {
  constructor() {
    super();
    this.formElement = null;
    this.submitButton = null;
    this.itemsContainer = null;
    this.zendeskUrl = this.dataset.zendeskUrl;
    this.basinEndpoint = this.dataset.basinEndpoint;
    this.redirectUrl = this.dataset.redirectUrl;
    this.sendingText = this.dataset.sendingText;
    this.submitText = this.dataset.submitText;
    this.errorHeading = this.dataset.errorHeading || 'Please adjust the following:';
    this.skuPlaceholder = this.dataset.skuPlaceholder || 'SKU';
    this.qtyPlaceholder = this.dataset.qtyPlaceholder || 'Qty';
    this.reasonPlaceholder = this.dataset.reasonPlaceholder || 'Reason';
    this.redirectUrl = this.dataset.redirectUrl;
    this._errorIdCounter = 0;
  }

  connectedCallback() {
    this.formElement = this.querySelector('#zendesk-returns-form');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.itemsContainer = this.querySelector('#items_to_return');
    this.statusElement = this.querySelector('.form-status');
    this.statusMessage = this.querySelector('.form-status__message');
    this.validatorElement = this.querySelector('wcag-form-validator');

    this.initializeDefaultRows();
    this.attachEventListeners();
  }

  attachEventListeners() {
    const addItemButton = this.querySelector('[data-action="add-item"]');
    if (addItemButton) {
      addItemButton.addEventListener('click', () => this.addItemRow());
    }

    // Add file input listeners
    const fileInputs = this.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        const fileNameElement = e.target.parentElement.querySelector('[data-file-name]');
        if (!fileNameElement) return;
        
        if (e.target.multiple) {
          const fileCount = e.target.files.length;
          fileNameElement.textContent = fileCount ? `${fileCount} file${fileCount > 1 ? 's' : ''} selected` : 'No files chosen';
        } else {
          fileNameElement.textContent = e.target.files[0]?.name || 'No file chosen';
        }
        this.clearFieldError(e.target);
      });
    });

    // this.formElement?.addEventListener('submit', (e) => this.handleSubmit(e));
    this.formElement?.querySelector('button[type="submit"]').addEventListener('click', (e) => this.handleSubmit(e));
  }

  initializeDefaultRows() {
    this.addItemRow();
  }

  addItemRow() {
    const template = this.querySelector('template[data-item-row]');
    if (template?.content?.firstElementChild) {
      const row = template.content.firstElementChild.cloneNode(true);
      row.querySelector('[data-action="remove-item"]')?.addEventListener('click', () => row.remove());
      this.itemsContainer.appendChild(row);
      return;
    }

    // Fallback (shouldn't be used if template exists)
    const fallback = document.createElement('div');
    fallback.className = 'grid grid-cols-1 gap-3 items-end border-b pb-spacing-md lg:grid-cols-12 border-border lg:px-spacing-md';
    fallback.innerHTML = `
      <div class="flex flex-col gap-2 lg:col-span-4">
        <label for="">SKU</label>
        <input name="item_sku" type="text" placeholder="${this.skuPlaceholder}" />
      </div>
      <div class="flex flex-col gap-2 lg:col-span-4">
        <label for="">Qty</label>
        <input name="item_qty" type="number" min="1" placeholder="${this.qtyPlaceholder}" />
      </div>
      <div class="flex relative flex-col gap-2 lg:col-span-4">
        <label for="">Reason</label>
        <select name="item_reason">
          <option value="" selected disabled>${this.reasonPlaceholder}</option>
        </select>
        <button type="button" class="absolute top-0 right-0 hover:text-red" aria-label="Remove" data-action="remove-item">×</button>
      </div>
    `;
    fallback.querySelector('[data-action="remove-item"]').addEventListener('click', () => fallback.remove());
    this.itemsContainer.appendChild(fallback);
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.validatorElement?.clear();
    const errors = this.validatorElement ? this.validatorElement.validate() : [];
    if (errors?.length) {
      this.validatorElement.showErrors(errors);
      return;
    }
    this.setLoading(true);

    try {
      if (this.basinEndpoint) {
        const formData = this.getBasinFormData();
        const response = await this.submitToBasin(formData);
        if (!response.ok) throw new Error('Failed to submit');
      } else if (this.zendeskUrl) {
        const files = this.collectSelectedFiles();
        const uploadTokens = await this.uploadAttachments(files);
        const payload = this.getZendeskPayload(uploadTokens);
        const response = await this.submitToZendesk(payload);
        if (!response.ok) throw new Error('Failed to submit');
      } else {
        throw new Error('No submission endpoint configured');
      }

      this.formElement.reset();
      this.itemsContainer.innerHTML = '';
      this.initializeDefaultRows();
      if (this.redirectUrl) window.location.href = this.redirectUrl;
    } catch (error) {
      this.showError(error);
    } finally {
      this.setLoading(false);
    }
  }


  getZendeskPayload(uploadTokens = []) {
    const form = this.formElement;
    const firstName = form.querySelector('#first_name')?.value?.trim() || '';
    const lastName = form.querySelector('#last_name')?.value?.trim() || '';
    const email = form.querySelector('#email')?.value?.trim() || '';
    const orderNumber = form.querySelector('#order_number')?.value?.trim() || '';
    const purchasedAt = form.querySelector('#purchased_at')?.value || '';
    const purchasedDate = form.querySelector('#purchased_date')?.value || '';
    const proofOfPurchase = form.querySelector('#proof_of_purchase')?.files || [];
    const itemPhotos = form.querySelector('#item_photos')?.files || [];
    const locale = form.querySelector('#zendesk-locale')?.value || '';

    const items = Array.from(this.itemsContainer.querySelectorAll('div.grid')).map(row => ({
      sku: row.querySelector('input[name="item_sku"]').value.trim(),
      qty: Number(row.querySelector('input[name="item_qty"]').value) || 1,
      reason: row.querySelector('select[name="item_reason"]').value
    })).filter(item => item.sku);

    const summary = this.buildSummaryText({ firstName, lastName, email, orderNumber, purchasedAt, purchasedDate, items, proofOfPurchase, itemPhotos });

    return {
      request: {
        requester: {
          name: `${firstName} ${lastName}`.trim(),
          email,
          locale
        },
        subject: `Returns Form Submission`,
        comment: {
          body: summary,
          ...(uploadTokens.length > 0 ? { uploads: uploadTokens } : {})
        },
        tags: ['return_request'],
        via: { channel: 'web_form' }
      }
    };
  }

  collectSelectedFiles() {
    const form = this.formElement;
    const proofOfPurchase = form.querySelector('#proof_of_purchase')?.files || [];
    const itemPhotos = form.querySelector('#item_photos')?.files || [];
    return [...Array.from(proofOfPurchase), ...Array.from(itemPhotos)];
  }

  getBasinFormData() {
    const form = this.formElement;
    const firstName = form.querySelector('#first_name')?.value?.trim() || '';
    const lastName = form.querySelector('#last_name')?.value?.trim() || '';
    const email = form.querySelector('#email')?.value?.trim() || '';
    const orderNumber = form.querySelector('#order_number')?.value?.trim() || '';
    const purchasedAt = form.querySelector('#purchased_at')?.value || '';
    const purchasedDate = form.querySelector('#purchased_date')?.value || '';

    const items = Array.from(this.itemsContainer.querySelectorAll('div.grid')).map(row => ({
      sku: row.querySelector('input[name="item_sku"]').value.trim(),
      qty: Number(row.querySelector('input[name="item_qty"]').value) || 1,
      reason: row.querySelector('select[name="item_reason"]').value
    })).filter(item => item.sku);
    const itemsSummary = this.buildBasinItemsText(items);

    // Basin-facing summary: keep order details + our custom item layout
    const summaryLines = [];
    if (orderNumber) summaryLines.push(`Order Number: ${orderNumber}`);
    if (purchasedAt) summaryLines.push(`Purchased At: ${purchasedAt}`);
    if (purchasedDate) summaryLines.push(`Purchased Date: ${purchasedDate}`);
    if (summaryLines.length) summaryLines.push('');
    summaryLines.push(itemsSummary);
    const summary = summaryLines.join('\n');

    const fd = new FormData();
    fd.append('first_name', firstName);
    fd.append('last_name', lastName);
    fd.append('email', email);
    fd.append('order_number', orderNumber);
    fd.append('purchased_at', purchasedAt);
    fd.append('purchased_date', purchasedDate);
    // Include structured JSON as an additional field for convenience
    for (const item of items) {
      fd.append(`SKU: ${item.sku} | qty: ${item.qty}`, item.reason);
    }
    fd.append('items_summary', itemsSummary);
    fd.append('summary', summary);


    // Attach files - Basin accepts multipart form-data uploads
    const files = this.collectSelectedFiles();
    files.forEach((file) => {
      // use a generic attachments[] field name for multiple files
      fd.append('attachments[]', file, file.name);
    });

    return fd;
  }

  buildBasinItemsText(items) {
    if (!items?.length) return '';
    const lines = [];
    items.forEach((item) => {
      const qty = Number(item.qty) || 1;
      const reason = item.reason || 'Other-Unspecified';
      lines.push(`Sku: ${item.sku} | qty: ${qty}`);
      lines.push(reason);
      lines.push('');
    });
    return lines.join('\n').trim();
  }

  async uploadAttachments(files) {
    if (!files?.length) return [];
    const uploads = await Promise.all(files.map((file) => this.uploadSingleFile(file).catch(() => null)));
    return uploads
      .map((res) => {
        if (!res) return '';
        if (typeof res === 'string') return res;
        if (res.upload?.token) return res.upload.token;
        if (res.token) return res.token;
        if (res.uploadToken) return res.uploadToken;
        return '';
      })
      .filter(Boolean);
  }

  async uploadSingleFile(file) {
    const url = `https://${this.zendeskUrl}/api/v2/uploads.json?filename=${encodeURIComponent(file.name)}`;
    const headers = {};
    if (file.type) headers['Content-Type'] = file.type;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: file
    });
    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  }

  buildSummaryText(data) {
    const { orderNumber, purchasedAt, purchasedDate, items } = data;
    const lines = [];
    if (orderNumber) lines.push(`Order Number: ${orderNumber}`);
    if (purchasedAt) lines.push(`Purchased At: ${purchasedAt}`);
    if (purchasedDate) lines.push(`Purchased Date: ${purchasedDate}`);
    lines.push('');
    items.forEach((item) => {
      lines.push(`SKU: ${item.sku}`);
      lines.push(`Quantity: ${item.qty}`);
      lines.push(`Reason: ${item.reason || 'Other-Unspecified'}`);
      lines.push('');
    });
    return lines.join('\n');
  }

  async submitToZendesk(formData) {
    const requestUrl = 'https://' + this.zendeskUrl + '/api/v2/requests.json';
    return fetch(requestUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
  }

  async submitToBasin(formData) {
    return fetch(this.basinEndpoint, {
      method: 'POST',
      body: formData
    });
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.textContent = isLoading ? this.sendingText : this.submitText;
  }

  showError(error) {
    if (!this.statusElement) return;
    this.statusElement.hidden = false;
    this.statusElement.classList.add('form-status--error');
    this.statusElement.classList.remove('form-status--success');
    this.statusMessage.textContent = 'Sorry, there was an error submitting your message. Please try again.';
    console.error('Returns form submission error:', error);
  }
}

customElements.define('zendesk-returns-form', ZendeskReturnsForm);


